import { passkeyErrorMessage } from '../lib/passkeyErrorMessage';
import { useEffect, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type MouseEvent as ReactMouseEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  completeCognitoSession,
  isDeviceTrusted, resumeTrustedSession, getPasskeyHint, isPasskeySupported,
  startPasskeySignIn, completePasskeySignIn, requestOptionsFromJSON, credentialToJSON,
  requestEmailOtp,
  verifyEmailOtp,
  type CognitoOtpChallenge,
} from '@darice/auth-client';
import { hubBrandFor } from '../lib/hubBrand';
import { postSignIn } from '../lib/postSignIn';

// Some environments fail to run the browser's implicit form submission when
// Enter is pressed inside these inputs (reported 2026-07-28 with the email
// field), so Enter submits explicitly.
// Apple only offers its code-from-email suggestion on a fresh focus event, so
// a click into an already-focused field appears dead while waiting for the
// email (reported 2026-07-28). Re-clicking now cycles blur->focus so every
// click re-triggers the suggestion scan.
function refreshAutofillOnReclick(event: ReactMouseEvent<HTMLInputElement>) {
  const input = event.currentTarget;
  if (document.activeElement !== input) return;
  window.setTimeout(() => {
    input.blur();
    window.setTimeout(() => input.focus(), 30);
  }, 0);
}

function submitFormOnEnter(event: ReactKeyboardEvent<HTMLInputElement>) {
  if (event.key !== 'Enter') return;
  event.preventDefault();
  const form = event.currentTarget.form;
  if (!form) return;
  if (typeof form.requestSubmit === 'function') form.requestSubmit();
  else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
}


type Step = 'email' | 'code';

export default function SignIn() {
  const location = useLocation();
  const navigate = useNavigate();
  const return_to = new URLSearchParams(location.search).get('return_to');
  const hub = hubBrandFor(return_to);
  useEffect(() => {
    if (hub === null) return;
    const previousTitle = document.title;
    document.title = hub === 'my4mlife' ? 'Your review hub · Sign in' : 'Your shared hub · Sign in';
    return () => { document.title = previousTitle; };
  }, [hub]);
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState<CognitoOtpChallenge | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [resuming, setResuming] = useState(() => isDeviceTrusted());
  const [passkeyEmail, setPasskeyEmail] = useState<string | null>(null);
  useEffect(() => {
    let active = true;
    const hint = getPasskeyHint();
    if (hint) void isPasskeySupported().then(supported => {
      if (active && supported) setPasskeyEmail(hint.email);
    });
    if (isDeviceTrusted()) void resumeTrustedSession().then(resumed => {
      if (!active) return;
      if (resumed) postSignIn(location.search, navigate, false);
      else setResuming(false);
    });
    else setResuming(false);
    return () => { active = false; };
  }, [location.search, navigate]);

  async function handlePasskey() {
    if (!passkeyEmail) return;
    setIsLoading(true);
    setError(null);
    try {
      const challenge = await startPasskeySignIn(passkeyEmail);
      const credential = await navigator.credentials.get({ publicKey: requestOptionsFromJSON(challenge.requestOptions) });
      if (!credential) throw new DOMException('Passkey cancelled', 'NotAllowedError');
      const tokens = await completePasskeySignIn({ ...challenge, credentialJson: credentialToJSON(credential as PublicKeyCredential) });
      await completeCognitoSession(tokens.idToken, tokens.refreshToken, tokens.accessToken);
      postSignIn(location.search, navigate, true);
    } catch (error) {
      setError(passkeyErrorMessage(error, 'We could not sign you in with that passkey. Please use an email code.'));
    } finally { setIsLoading(false); }
  }


  async function handleEmailSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsLoading(true);
    if (!email.trim()) {
      setIsLoading(false);
      setError('Enter your email address.');
      return;
    }
    try {
      const nextChallenge = await requestEmailOtp(email);
      setChallenge(nextChallenge);
      setEmail(nextChallenge.email);
      setStep('code');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }

  async function handleCodeSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setIsLoading(true);
    try {
      if (!challenge) throw new Error('FAIL: request a verification code first');
      const tokens = await verifyEmailOtp(challenge, code);
      await completeCognitoSession(tokens.idToken, tokens.refreshToken, tokens.accessToken);
      postSignIn(location.search, navigate, true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <main className={`auth-shell${hub === 'heather-nelson' ? ' heather-hub-auth' : hub === 'my4mlife' ? ' my4mlife-hub-auth' : ''}`}>
      <section className="signin-panel" aria-labelledby="signin-title">
        <h1 className="sr-only" id="signin-title">{hub === 'my4mlife' ? 'Sign in to your review hub' : hub === 'heather-nelson' ? 'Sign in to your shared hub' : 'Sign in to Darice'}</h1>
        {hub === 'heather-nelson' ? <header className="hub-welcome">
          <div className="hub-marks">
            <div className="hub-mark hub-red-skies-mark"><img src="/brands/heather-hub/rsy-her-lotus-lockup-reversed-dark.svg" alt="Red Skies Yoga" /></div>
            <div className="hub-mark hub-guardian-mark"><img src="/brands/heather-hub/guardian-bracket-lockup-reversed-dark.svg" alt="Guardian Title and Trust" /></div>
            <div className="hub-mark hub-heather-mark"><img src="/brands/heather-hub/heather-monogram-lockup-a-warm-gold-dark.svg" alt="Heather Nelson" /><small>Practice &amp; Precision</small></div>
          </div>
          <h2>Welcome.</h2>
          <p>Your studio, your businesses and the people at the heart of them, in one place that stays current.</p>
        </header> : hub === 'my4mlife' ? <header className="hub-welcome">
          <p className="hub-wordmark">TJ<span>/</span>My4MLife</p>
          <div className="hub-rule" aria-hidden="true"></div>
          <h2>Welcome.</h2>
          <p>The person. The purpose. <b>The business we build.</b></p>
        </header> : <p className="login-wordmark" aria-hidden="true">
          <span>Darice</span> <em>Strategies</em>
        </p>}

        {resuming ? <p role="status">Signing you in…</p> : <>
        {step === 'email' && passkeyEmail && <div className="form-stack">
          <button className="primary-action" disabled={isLoading} onClick={handlePasskey}>Sign in with Face ID or Touch ID</button>
          <p>Or get an email code</p>
        </div>}
        {step === 'email' ? (
          <form className="form-stack" onSubmit={handleEmailSubmit}>
            {/* On the Darice screen, "Email" is hidden by request (Justine 2026-08-06) —
                the placeholder carries the meaning. The label itself stays for
                screen readers and autofill, just visually hidden; deleting it
                outright would leave the field unnamed to assistive tech. */}
            <label className={hub !== null ? 'field-label' : 'sr-only'} htmlFor="email">{hub !== null ? 'Your email' : 'Email'}</label>
            <input
              autoComplete="username"
              autoFocus
              className="text-input"
              id="email"
              inputMode="email"
              name="email"
              onKeyDown={submitFormOnEnter}
              onChange={(event) => setEmail(event.target.value)}
              placeholder={hub !== null ? 'Your email' : 'you@example.com'}
              type="email"
              value={email}
            />
            <button
              className="primary-action"
              data-testid="signin-button"
              disabled={isLoading}
              type="submit"
            >
              {/* Plain English on purpose — the locked Latin (Tessera / Vox arcana... /
                  Intrāre) belongs to the code step below, per the lock note there.
                  "Perge" was undocumented drift in a session-close commit (57fb40c),
                  reverted 2026-08-12 the day Justine spotted it on the live page. */}
              {isLoading ? 'Continuing...' : 'Continue'}
            </button>
          </form>
        ) : (
          <form className="form-stack" onSubmit={handleCodeSubmit}>
            <p className="step-meta">{email}</p>
            <p className="step-meta">If that address is registered, a code is on its way. No email? Check the address and try again.</p>
            {/* Locked login vocabulary (Tessera / Vox arcana... / Intrāre) carried
                over from the Vercel app. The email step stays plain English — the
                three locked words belong to the act of entering, and inventing new
                Latin for a step the lock never covered would be improvising. */}
            <label className="field-label" htmlFor="verification-code">{hub !== null ? 'Your sign-in code' : 'Tessera'}</label>
            <input
              aria-describedby="verification-code-hint"
              autoComplete="username"
              autoFocus
              className="text-input"
              id="verification-code"
              inputMode="numeric"
              maxLength={6}
              name="email"
              onMouseDown={refreshAutofillOnReclick}
              onKeyDown={submitFormOnEnter}
              onChange={(event) => setCode(event.target.value)}
              placeholder={hub !== null ? 'Six-digit code' : 'Vox arcana...'}
              type="text"
              value={code}
            />
            <p className="field-hint" id="verification-code-hint">
              The six-digit code from your email.
            </p>
            <button
              className="primary-action"
              data-testid="signin-button"
              disabled={isLoading}
              type="submit"
            >
              {isLoading ? 'Verifying...' : hub !== null ? 'Open your hub' : 'Intrāre'}
            </button>
            <button
              className="secondary-action"
              disabled={isLoading}
              onClick={() => {
                setChallenge(null);
                setCode('');
                setError(null);
                setStep('email');
              }}
              type="button"
            >
              Change email
            </button>
          </form>
        )}

        </>}
        {error && (
          <p className="auth-error" data-testid="signin-error" role="alert">
            {error}
          </p>
        )}
        {hub !== null && <p className="hub-auth-credit">Prepared by Darice <em>Strategies</em></p>}
      </section>
    </main>
  );
}
